import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Signature, ShieldCheck } from "@phosphor-icons/react";

// Blocks the carrier portal until the carrier e-signs the current agreement version.
export default function AgreementResignModal() {
  const [st, setSt] = useState(null);
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showFull, setShowFull] = useState(false);

  useEffect(() => { api.get("/my/agreement-status").then(({ data }) => setSt(data)).catch(() => {}); }, []);
  if (!st?.needs_resign) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!agree) return toast.error("Please confirm you have read and agree");
    setBusy(true);
    try {
      await api.post("/my/agreement/accept", { typed_name: name, title, agree });
      toast.success(`Agreement v${st.current_version} accepted — thank you`);
      setSt({ ...st, needs_resign: false });
    } catch (err) { toast.error(formatApiErrorDetail(err, "Could not record your signature")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm grid place-items-center p-3 sm:p-6" data-testid="agreement-resign-modal">
      <form onSubmit={submit} className="bg-white text-black rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="px-5 sm:px-7 pt-6 pb-4 border-b border-black/10">
          <div className="overline text-[#0A84FF] mb-1 flex items-center gap-1.5"><Signature size={14} weight="fill" /> Updated agreement · v{st.current_version}</div>
          <h2 className="font-display font-black text-2xl sm:text-3xl text-black">Please review and e-sign to continue</h2>
          <p className="text-xs text-neutral-500 mt-1">
            {st.signed_version ? `You last signed v${st.signed_version}.` : "We don't have a signed copy of the current agreement on file for your account."} Here's what changed:
          </p>
        </div>
        <div className="px-5 sm:px-7 py-4 overflow-y-auto flex-1 min-h-0">
          <ul className="space-y-2 mb-4" data-testid="agreement-changes">
            {st.changes.map((c, i) => (
              <li key={i} className="flex gap-2 text-sm text-neutral-800"><ShieldCheck size={16} weight="fill" className="text-[#0A84FF] shrink-0 mt-0.5" /><span>{c}</span></li>
            ))}
          </ul>
          <button type="button" onClick={() => setShowFull((v) => !v)} className="text-xs font-bold text-[#0A84FF] !min-h-0 py-1" data-testid="agreement-toggle-full">
            {showFull ? "Hide full agreement" : "Read the full agreement"}
          </button>
          {showFull && (
            <div className="mt-3 rounded-xl border border-black/10 bg-neutral-50 p-4 space-y-3 text-[13px] leading-relaxed text-neutral-800" data-testid="agreement-full-text">
              {st.sections.map((sec, i) => (
                <div key={i}>
                  {sec.heading && <div className="font-black text-black text-xs tracking-wide mb-1">{sec.heading}</div>}
                  {sec.body.map((p, j) => <p key={j} className="mb-1.5">{p}</p>)}
                </div>
              ))}
            </div>
          )}
          <div className="mt-5 grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Typed signature (full legal name) *</span>
              <input value={name} onChange={(e) => setName(e.target.value)} required minLength={3} data-testid="agreement-typed-name"
                className="mt-1 w-full border border-black/15 rounded-lg px-3 py-2 text-sm font-display italic text-black focus:outline-none focus:border-[#0A84FF]" placeholder="Your name" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Title</span>
              <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="agreement-title"
                className="mt-1 w-full border border-black/15 rounded-lg px-3 py-2 text-sm text-black focus:outline-none focus:border-[#0A84FF]" placeholder="e.g. Owner, President" />
            </label>
          </div>
          <label className="mt-4 flex items-start gap-2 text-xs text-neutral-700 cursor-pointer">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5" data-testid="agreement-agree" />
            <span>I have read the Independent Carrier-Dispatcher Service Agreement v{st.current_version} and agree to it on behalf of my company. Typing my name is a legally binding electronic signature under the E-SIGN Act; my IP address and timestamp will be recorded.</span>
          </label>
        </div>
        <div className="px-5 sm:px-7 py-4 border-t border-black/10 flex items-center justify-between gap-3 flex-wrap">
          <span className="text-[11px] text-neutral-500">Questions? Dispatch@MontanaFreightGroup.com</span>
          <button type="submit" disabled={busy || !agree || name.trim().length < 3} data-testid="agreement-accept-btn"
            className="bg-[#0A84FF] hover:bg-[#0869c7] text-white text-sm font-bold px-5 py-2.5 rounded-lg disabled:opacity-50">
            {busy ? "Recording…" : "Accept & e-sign"}
          </button>
        </div>
      </form>
    </div>
  );
}
